import { useState, useCallback } from 'preact/hooks';
import { MainMenu } from '../screens/MainMenu';
import { LevelSelect } from '../screens/LevelSelect';
import { GamePlay } from '../screens/GamePlay';
import { Profile } from '../screens/Profile';
import { DailyPuzzle } from '../screens/DailyPuzzle';
import { useGameState } from '../utils/useGameState';
import { getTotalLevelCount } from '../game/levels';

export type Screen = 'menu' | 'levels' | 'game' | 'profile' | 'daily';

export function App() {
  const [screen, setScreen] = useState<Screen>('menu');
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [selectedWorld, setSelectedWorld] = useState(1);
  const [isDaily, setIsDaily] = useState(false);
  const gameState = useGameState();

  const navigateTo = useCallback((s: Screen) => setScreen(s), []);

  const playLevel = useCallback((levelId: number) => {
    setSelectedLevel(levelId);
    setIsDaily(false);
    setScreen('game');
  }, []);

  const playDailyLevel = useCallback((levelId: number) => {
    setSelectedLevel(levelId);
    setIsDaily(true);
    setScreen('game');
  }, []);

  const selectWorld = useCallback((worldId: number) => {
    setSelectedWorld(worldId);
    setScreen('levels');
  }, []);

  return (
    <div style={styles.container}>
      {screen === 'menu' && (
        <MainMenu
          onPlay={() => {
            const nextLevel = gameState.getNextUncompletedLevel();
            playLevel(nextLevel);
          }}
          onLevelSelect={() => navigateTo('levels')}
          onDaily={() => navigateTo('daily')}
          onProfile={() => navigateTo('profile')}
          gameState={gameState}
        />
      )}
      {screen === 'levels' && (
        <LevelSelect
          selectedWorld={selectedWorld}
          onSelectWorld={selectWorld}
          onPlayLevel={playLevel}
          onBack={() => navigateTo('menu')}
          gameState={gameState}
        />
      )}
      {screen === 'game' && (
        <GamePlay
          levelId={selectedLevel}
          isDaily={isDaily}
          onBack={() => navigateTo(isDaily ? 'daily' : 'levels')}
          onNextLevel={() => {
            const next = selectedLevel + 1;
            if (next <= getTotalLevelCount()) {
              playLevel(next);
            } else {
              navigateTo('menu');
            }
          }}
          onMenu={() => navigateTo('menu')}
          gameState={gameState}
        />
      )}
      {screen === 'daily' && (
        <DailyPuzzle
          onBack={() => navigateTo('menu')}
          onPlayLevel={playDailyLevel}
          gameState={gameState}
        />
      )}
      {screen === 'profile' && (
        <Profile
          onBack={() => navigateTo('menu')}
          gameState={gameState}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
  },
};
